import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getTags, deleteTag, Tag } from '../../shared/api/tags';
import { useAuth } from '../../shared/auth/AuthContext';
import { getAdminLoginPath, getAdminTagsPath } from '../../shared/config/env';
import AdminSidebar from '../components/AdminSidebar';
import './TagList.css';

function TagList() {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingTagId, setDeletingTagId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filteredTags, setFilteredTags] = useState<Tag[]>([]);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const adminLoginPath = getAdminLoginPath();
  const adminTagsPath = getAdminTagsPath();

  const fetchTags = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getTags();
      // idで昇順ソート
      const sortedTags = [...response.tags].sort((a, b) => {
        return a.id.localeCompare(b.id);
      });
      setTags(sortedTags);
    } catch (err) {
      if (err instanceof Error && (err as any).isAuthError) {
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('タグの取得に失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, []);

  // 検索処理
  const handleSearch = useCallback(() => {
    if (!searchQuery.trim()) {
      // 検索クエリが空の場合は全件表示
      setFilteredTags(tags);
      return;
    }

    const query = searchQuery.trim().toLowerCase();
    const filtered = tags.filter((tag) => {
      // 名前（日本語）、名前（英語）、slugで検索
      const nameMatch = tag.name.toLowerCase().includes(query);
      const nameEnMatch = tag.nameEn?.toLowerCase().includes(query) || false;
      const slugMatch = tag.slug.toLowerCase().includes(query);
      return nameMatch || nameEnMatch || slugMatch;
    });
    setFilteredTags(filtered);
  }, [searchQuery, tags]);

  // タグが更新されたら、検索結果も更新
  useEffect(() => {
    if (searchQuery.trim()) {
      handleSearch();
    } else {
      setFilteredTags(tags);
    }
  }, [tags, searchQuery, handleSearch]);

  // 表示するタグリスト（検索クエリがある場合は検索結果、ない場合は全件）
  const displayTags = useMemo(() => {
    return searchQuery.trim() ? filteredTags : tags;
  }, [searchQuery, filteredTags, tags]);

  const handleDelete = async (tag: Tag) => {
    const confirmed = window.confirm(
      `「${tag.name}」を削除してもよろしいですか？\n\nこの操作は取り消せません。`
    );

    if (!confirmed) {
      return;
    }

    try {
      setDeletingTagId(tag.id);
      await deleteTag(tag.id);
      await fetchTags();
    } catch (err) {
      if (err instanceof Error && (err as any).isAuthError) {
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      if (err instanceof Error) {
        alert(`削除に失敗しました: ${err.message}`);
      } else {
        alert('削除に失敗しました');
      }
    } finally {
      setDeletingTagId(null);
    }
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-layout__main">
        <div className="tag-list">
          <header className="tag-list__header">
            <h1 className="tag-list__title">タグ管理</h1>
            <div className="tag-list__actions">
              <Link to={`${adminTagsPath}/new`} className="tag-list__button tag-list__button--primary">
                新規作成
              </Link>
            </div>
          </header>
          <div className="tag-list__search">
            <input
              type="text"
              className="tag-list__search-input"
              placeholder="タグ名、英語名、スラッグで検索"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
            <button
              type="button"
              className="tag-list__button tag-list__button--search"
              onClick={handleSearch}
            >
              検索
            </button>
            {searchQuery.trim() && (
              <button
                type="button"
                className="tag-list__button tag-list__button--clear"
                onClick={() => {
                  setSearchQuery('');
                  setFilteredTags(tags);
                }}
              >
                クリア
              </button>
            )}
          </div>
          <main className="tag-list__content">
            {loading ? (
              <p className="tag-list__loading">読み込み中...</p>
            ) : error ? (
              <p className="tag-list__error">{error}</p>
            ) : displayTags.length === 0 ? (
              <p className="tag-list__empty">
                {searchQuery.trim() ? '検索結果がありません' : 'タグがありません'}
              </p>
            ) : (
              <div className="tag-list__table-container">
                <table className="tag-list__table">
                  <thead>
                    <tr>
                      <th className="tag-list__th">ID</th>
                      <th className="tag-list__th">名前（日本語）</th>
                      <th className="tag-list__th">名前（英語）</th>
                      <th className="tag-list__th">スラッグ</th>
                      <th className="tag-list__th">表示</th>
                      <th className="tag-list__th">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayTags.map((tag) => (
                      <tr key={tag.id} className="tag-list__row">
                        <td className="tag-list__td">
                          <span className="tag-list__id">{tag.id}</span>
                        </td>
                        <td className="tag-list__td">
                          <Link
                            to={`${adminTagsPath}/${tag.id}/edit`}
                            className="tag-list__link"
                          >
                            {tag.name}
                          </Link>
                        </td>
                        <td className="tag-list__td">
                          {tag.nameEn || '-'}
                        </td>
                        <td className="tag-list__td">
                          <code className="tag-list__slug">{tag.slug}</code>
                        </td>
                        <td className="tag-list__td">
                          <span className={`tag-list__visibility-badge ${
                            tag.isVisible 
                              ? 'tag-list__visibility-badge--visible' 
                              : 'tag-list__visibility-badge--hidden'
                          }`}>
                            {tag.isVisible ? '表示' : '非表示'}
                          </span>
                        </td>
                        <td className="tag-list__td">
                          <div className="tag-list__actions-cell">
                            <button
                              className="tag-list__button tag-list__button--delete"
                              onClick={() => handleDelete(tag)}
                              disabled={deletingTagId === tag.id}
                            >
                              {deletingTagId === tag.id ? '削除中...' : '削除'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

export default TagList;

